import { getBusyFilter, getConflictingEmployees } from "@/components/event/event-availability.ts";
import { EventAvailabilityPanel } from "@/components/event/EventAvailabilityPanel.tsx";
import "@/components/event/event-editor.css";
import { Datepicker } from "@/components/datepicker/Datepicker.tsx";
import { buildEventSchema, EventSchema } from "@/components/event/event.schema.ts";
import { TimePicker } from "@/components/time-picker/TimePicker.tsx";
import { TimePickerMode } from "@/components/time-picker/time-picker.types.ts";
import { getTimeId } from "@/helpers/dates.helper.ts";
import { ServerErrorField } from "@/components/server-error-field/ServerErrorField.tsx";
import useFormatter, { formatDurationTitle } from "@/hooks/use-formatter.hook.ts";
import { RuleGroupsEditor } from "@/components/apply-to/RuleGroupsEditor.tsx";
import useEmployeeSearchInline, {
  useEmployeesByIds,
} from "@/queries/employees/use-employees.query.ts";
import {
  useCandidateActionCreate,
  useCandidateActionUpdate,
} from "@/queries/recruit/use-candidate-actions.query.ts";
import { useCalendars } from "@/queries/use-calendars.query.ts";
import { useEmployeesBusy, useEventsSave } from "@/queries/use-events.query.ts";
import { useRuleConditionTypes } from "@/queries/use-rules.query.ts";
import analyticsService, {
  analyticEvents,
  analyticProperties,
} from "@/services/analytics-service.ts";
import getTimezones, { DefaultTimezone } from "@/services/timezone-service.ts";
import { ApiActionPayload, ApiActionVisibilities } from "@/types/action.types.ts";
import { ApiConferenceProviders } from "@/types/calendar.types.ts";
import { Time } from "@/types/common.types.ts";
import { EmployeeFilterDefault, getEmployeeAvatarUrl } from "@/types/employees/employee.types.ts";
import {
  ApiEventAttendee,
  ApiEventAttendeeResponseStatuses,
  ApiEventEdit,
  ApiEventSavePayload,
  ApiEventTypes,
  ApiEventVisibility,
  getDescriptionForAttendee,
} from "@/types/event.types.ts";
import { Rule, RuleShort, RuleApplySignType, RuleConditionType } from "@/types/rule.types.ts";
import { RuleRegistryPlace } from "@/types/rule-whitelists.ts";
import { zodResolver } from "@hookform/resolvers/zod";
import { useFloating, useTransitionStatus } from "@floating-ui/react";
import {
  Box,
  Button,
  DotSeparator,
  DropdownMenu,
  DropdownMenuItem,
  FontAwesomeIcon,
  Input,
  LinkButtonTo,
  ListItem,
  Modal,
  ModalProps,
  Select,
  Stack,
  Switch,
  Text,
  Textarea,
  Title,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
  useModals,
  useToasts,
} from "@skailer-com/uikit";
import { add, addDays, differenceInMinutes, set, startOfDay } from "date-fns";
import { fromZonedTime, toZonedTime } from "date-fns-tz";
import { useCallback, useEffect, useMemo, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";

export interface EventInEditModalProps extends ModalProps {
  event: ApiEventEdit;
  eventId?: number;
}

function toRuleShort(rule: Rule): RuleShort {
  return {
    condition_type: rule.condition_type,
    sign: rule.sign,
    condition_ids: rule.condition_ids,
    value_ids: rule.value_ids,
    date_condition: rule.date_condition,
  };
}

export function EventEditModal({ event, eventId, ...props }: EventInEditModalProps) {
  const { t, i18n } = useTranslation();
  const { renderTime } = useFormatter();
  const { renderEmployeeName } = useFormatter();
  const { showError } = useToasts();
  const { close } = useModals();
  const { id } = { ...props };

  const EventSchemaObject = buildEventSchema();

  const [calendar, setCalendar] = useState(event.calendar);

  const {
    register,
    control,
    handleSubmit,
    setValue,
    getValues,
    watch,
    formState: { errors, dirtyFields },
  } = useForm<EventSchema>({
    mode: "onSubmit",
    resolver: zodResolver(EventSchemaObject),
    values: {
      title: event.title,
      timezone: event.timezone,
      calendar_id: event.calendar.id,
      description: event.description,
      visibility: event.visibility,
      conference_provider: event.conference_provider,
      datetime_start: event.is_all_day
        ? event.datetime_start
        : toZonedTime(event.datetime_start, event.timezone),
      datetime_end: event.is_all_day
        ? event.datetime_end
        : toZonedTime(event.datetime_end, event.timezone),
      is_all_day: event.is_all_day,
      attendee_rule_groups: event.attendeeGroups ?? [],
    },
  });

  const dateStartWatch = watch("datetime_start");
  const dateEndWatch = watch("datetime_end");
  const isAllDayWatch = watch("is_all_day");
  const timezoneWatch = watch("timezone") || DefaultTimezone;

  const durationInMinutes = useMemo(() => {
    return differenceInMinutes(dateEndWatch, dateStartWatch);
  }, [dateStartWatch, dateEndWatch]);

  const [attendees, setAttendees] = useState<ApiEventAttendee[]>(event.attendees);
  const [isAvailabilityOpen, setIsAvailabilityOpen] = useState(false);
  const hasEmployeeGuest = attendees.some(
    (attendee) => attendee.employee && !attendee.is_organizer
  );
  const isOpen = isAvailabilityOpen && hasEmployeeGuest;
  const { refs: availabilityRefs, context: availabilityContext } = useFloating({
    open: isOpen,
  });
  const { isMounted: isExpanded, status: availabilityStatus } = useTransitionStatus(
    availabilityContext,
    { duration: 150 }
  );
  const selectedStart = fromZonedTime(
    isAllDayWatch ? startOfDay(dateStartWatch) : dateStartWatch,
    timezoneWatch
  );
  const endDay = startOfDay(dateEndWatch);
  const selectedEnd = fromZonedTime(
    isAllDayWatch
      ? endDay > startOfDay(dateStartWatch)
        ? endDay
        : addDays(startOfDay(dateStartWatch), 1)
      : dateEndWatch,
    timezoneWatch
  );
  const guestAvailability = useEmployeesBusy(
    getBusyFilter(
      attendees.flatMap((attendee) => (attendee.employee ? [attendee.employee.id] : [])),
      dateStartWatch,
      toZonedTime(selectedEnd, timezoneWatch),
      timezoneWatch
    )
  );
  const conflictingEmployeeIds = new Set(
    !guestAvailability.isFetching && !guestAvailability.isError
      ? getConflictingEmployees(guestAvailability.data ?? [], selectedStart, selectedEnd).map(
          (person) => person.employee_id
        )
      : []
  );

  const { data: usersLoaded } = useEmployeesByIds(
    event.attendees.filter((x) => x.employee).map((x) => x.employee?.id || 0)
  );

  useEffect(() => {
    if (usersLoaded && usersLoaded.length > 0) {
      const prevAttendees = attendees;
      for (let i = 0; i < prevAttendees.length; i++) {
        if (prevAttendees[i].employee) {
          const user = usersLoaded.find((x) => x.id == prevAttendees[i].employee?.id);
          if (user) {
            prevAttendees[i].runtime_only_employee = user;
          }
        }
      }
      setAttendees(prevAttendees);
    }
  }, [usersLoaded]);

  const [suggestedAttendees, setSuggestedAttendees] = useState<ApiEventAttendee[]>(
    event.suggestedAttendees || []
  );

  const attendeeGroups = watch("attendee_rule_groups") ?? [];
  const attendeeConditionTypes = useRuleConditionTypes(RuleRegistryPlace.eventAttendees);

  const addFirstGroup = () => {
    setValue(
      "attendee_rule_groups",
      [
        {
          rules: [
            {
              condition_type: RuleConditionType.all_active_employees,
              sign: RuleApplySignType.include,
              condition_ids: [],
              condition_entities: [],
              value_entities: [],
            },
          ],
        },
      ],
      { shouldDirty: true }
    );
  };

  const employeesSearch = useEmployeeSearchInline(true, {
    ...EmployeeFilterDefault,
    exclude_ids: attendees.map((x) => x.employee?.id || 0),
  });
  const eventActionSave = useEventsSave(control);
  const candidateActionCreate = useCandidateActionCreate(control);
  const candidateActionUpdate = useCandidateActionUpdate(control);

  const { data: calendars } = useCalendars();
  const calendarFromList = calendars?.find((x) => x.id == calendar.id);
  const conferenceProviders = calendarFromList?.conference_providers ?? [];

  const conferenceOptions = useMemo(() => {
    return [
      { id: "none", title: t("calendar:no_conference") },
      ...(calendarFromList?.conference_providers ?? []).map((provider) => ({
        id: provider,
        title:
          provider === ApiConferenceProviders.zoom
            ? t("calendar:zoom")
            : calendarFromList?.conference_provider === "kontur"
              ? t("calendar:kontur_talk")
              : t("calendar:google_meet"),
      })),
    ];
  }, [calendarFromList, t]);

  useEffect(() => {
    employeesSearch.setFilter({
      ...employeesSearch.filter,
      exclude_ids: attendees.map((x) => x.employee?.id || 0),
    });
  }, [attendees]);

  useEffect(() => {
    if (calendars && calendars.length > 0 && event.calendar.id == 0) {
      setValue("calendar_id", calendars[0].id);
      setCalendar(calendars[0]);
    }
  }, [calendars, event]);

  const startTimes = useMemo(() => {
    const startTimes: Time[] = [];

    for (let i = 9; i < 20; i++) {
      const date1 = new Date(2000, 1, 1, i, 0);
      const date2 = new Date(2000, 1, 1, i, 30);

      startTimes.push({
        id: getTimeId(date1),
        title: renderTime(date1),
        date: date1,
      });

      startTimes.push({
        id: getTimeId(date2),
        title: renderTime(date2),
        date: date2,
      });
    }
    return startTimes;
  }, []);

  const [isCustomStartTime, setIsCustomStartTime] = useState(
    startTimes.find((x) => x.id == getTimeId(dateStartWatch)) == null
  );

  const durationTitle = useCallback(
    (minutes: number) => formatDurationTitle(minutes, i18n.resolvedLanguage ?? i18n.language),
    [i18n.language, i18n.resolvedLanguage]
  );

  const durationOptions = useMemo(() => {
    const standardDurations = [15, 30, 45, 60, 90, 120];
    const durations =
      durationInMinutes > 0 && !standardDurations.includes(durationInMinutes)
        ? [...standardDurations, durationInMinutes].sort((a, b) => a - b)
        : standardDurations;

    return [
      ...durations.map((minutes) => ({
        id: minutes,
        title: durationTitle(minutes),
        caption: renderTime(add(dateStartWatch, { minutes })),
      })),
      { id: "custom", title: t("common:other_time") },
    ];
  }, [dateStartWatch, durationInMinutes, durationTitle, renderTime, t]);

  const customEndTimePresets = useMemo<Time[]>(
    () =>
      [15, 30, 45, 60, 90, 120].map((minutes) => {
        const date = add(dateStartWatch, { minutes });

        return {
          id: getTimeId(date),
          title: durationTitle(minutes),
          caption: renderTime(date),
          date,
        };
      }),
    [dateStartWatch, durationTitle, renderTime]
  );

  const privacyOptions = useMemo(() => {
    return [
      { id: "public", title: t("calendar:event_visibility_public") },
      { id: "private", title: t("calendar:event_visibility_private") },
    ];
  }, []);

  const onSubmit = handleSubmit((schema) => {
    const timezone = schema.timezone || DefaultTimezone;
    const toInstant = (date: Date) => (schema.is_all_day ? date : fromZonedTime(date, timezone));
    const conferenceProvider = calendarFromList
      ? (calendarFromList.conference_providers.find((x) => x === schema.conference_provider) ??
        null)
      : (schema.conference_provider ?? null);
    const eventPayload: ApiEventSavePayload = {
      type: event.type,
      title: schema.title,
      timezone,
      description: schema.description || "",
      datetime_start: toInstant(schema.datetime_start),
      datetime_end: toInstant(schema.datetime_end),
      calendar_id: schema.calendar_id,
      visibility: schema.visibility || ApiEventVisibility.public,
      attendees: attendees.map((x) => ({
        employee_id: x.employee?.id,
        email: x.email,
        is_organizer: x.is_organizer,
      })),
      is_all_day: schema.is_all_day,
      is_conference: conferenceProvider === ApiConferenceProviders.google_meet,
      conference_provider: conferenceProvider,
      recurrence: event.recurrence,
    };

    const attendeeRuleGroups = (schema.attendee_rule_groups ?? [])
      .filter((group) => group.rules.length > 0)
      .map((group) => ({ rules: group.rules.map(toRuleShort) }));
    if (attendeeRuleGroups.length > 0) {
      eventPayload.attendee_rule_groups = attendeeRuleGroups;
    }

    if (event.type == ApiEventTypes.interview && event.interview && !eventId) {
      eventPayload.interview = {
        candidate_id: event.interview.candidate_id,
        job_id: event.interview.job_id,
        applicant_id: event.interview.applicant_id,
        hide_salary: event.interview.hide_salary,
      };

      const payload: ApiActionPayload = {
        event: eventPayload,
        visibility: ApiActionVisibilities.public,
      };

      candidateActionCreate.mutate(
        { id: event.interview.candidate_id, payload: payload },
        {
          onSuccess: () => {
            analyticsService.trackEvent(analyticEvents.events.created, {
              [analyticProperties.actionType]: "Candidate",
            });
            close(id);
          },
        }
      );
    } else {
      eventActionSave.mutateAsync(
        { id: eventId, payload: eventPayload },
        {
          onSuccess() {
            if (eventId) {
              analyticsService.trackEvent(analyticEvents.events.edited, {
                [analyticProperties.id]: eventId,
                [analyticProperties.actionType]: "Calendar",
              });
            } else {
              analyticsService.trackEvent(analyticEvents.events.created, {
                [analyticProperties.actionType]: "Calendar",
              });
            }

            close(id);
          },
        }
      );
    }
  });

  const getValueSlotForAttendee = (attendee: ApiEventAttendee) => {
    if (attendee.is_organizer) {
      return null;
    }

    return (
      <DropdownMenu>
        <DropdownMenuItem
          onClick={() => {
            if (attendee.employee) {
              setAttendees(attendees.filter((x) => x.employee?.id != attendee.employee?.id));
            } else {
              setAttendees(attendees.filter((x) => x.email != attendee.email));
            }
          }}
          title={t("calendar:remove_guest")}
        />
      </DropdownMenu>
    );
  };

  const saveActions = (
    <Stack gap="sm">
      <ServerErrorField errors={errors} />
      <Button
        onClick={isExpanded ? undefined : onSubmit}
        isLoading={
          eventActionSave.isPending ||
          candidateActionCreate.isPending ||
          candidateActionUpdate.isPending
        }
        size="lg"
        className="w-full"
        type={isExpanded ? "submit" : "button"}
      >
        {t("common:save")}
      </Button>
    </Stack>
  );

  return (
    <Modal
      {...props}
      layout={isExpanded ? "page" : "stretch"}
      size={isExpanded ? "full" : "md"}
      title={
        isExpanded
          ? undefined
          : eventId
            ? t("calendar:event_edit_title")
            : t("calendar:event_create_title")
      }
      actions={isExpanded ? undefined : saveActions}
      withCloser
      closeByEscEnabled
    >
      <div
        className={
          isExpanded
            ? "event-editor-layout flex h-full min-h-0 flex-col overflow-hidden lg:flex-row"
            : "contents"
        }
      >
        <form
          onSubmit={onSubmit}
          className={
            isExpanded
              ? "flex max-h-[55%] min-h-0 w-full shrink-0 flex-col overflow-y-auto overscroll-contain border-b border-dark/10 bg-light lg:h-full lg:max-h-none lg:w-[420px] lg:border-r lg:border-b-0"
              : "flex grow flex-col"
          }
        >
          <div className={isExpanded ? "flex grow flex-col px-6 pt-4 pb-6" : "flex grow flex-col"}>
            <Input
              label={t("common:title")}
              size={"lg"}
              type="text"
              {...register("title")}
              error={errors.title?.message}
            />
            <Title size={"xs"} header={t("common:date_and_time")} paddingTop paddingBottom />
            <Box className={"grid grid-cols-3 gap-sm"}>
              <Box className={"col-span-2"}>
                <Controller
                  control={control}
                  name="datetime_start"
                  render={({ field: { value, onChange } }) => (
                    <Datepicker
                      value={value}
                      onChange={(newValue) => {
                        if (newValue) {
                          const durationCache = durationInMinutes;
                          const isAllDay = getValues("is_all_day");
                          const currentStart = getValues("datetime_start") ?? newValue;
                          const nextStart = isAllDay
                            ? newValue
                            : set(newValue, {
                                hours: currentStart.getHours(),
                                minutes: currentStart.getMinutes(),
                                seconds: currentStart.getSeconds(),
                                milliseconds: currentStart.getMilliseconds(),
                              });

                          onChange(nextStart);

                          if (isAllDay) {
                            if (nextStart > getValues("datetime_end")) {
                              setValue("datetime_end", nextStart);
                            }
                          } else {
                            setValue("datetime_end", add(nextStart, { minutes: durationCache }));
                          }
                        }
                      }}
                      label={t("common:date")}
                      className={"col-span-2"}
                      error={errors.datetime_start?.message}
                    />
                  )}
                />
              </Box>
              <Box className={"self-center justify-self-end"}>
                <Controller
                  control={control}
                  name="is_all_day"
                  render={({ field: { value, onChange } }) => (
                    <Switch
                      checked={value}
                      onChange={(e) => {
                        onChange(e);
                      }}
                      title={t("calendar:event_all_day")}
                    />
                  )}
                />
              </Box>
            </Box>
            {isAllDayWatch && (
              <Controller
                control={control}
                name="datetime_end"
                render={({ field: { value, onChange } }) => (
                  <Datepicker
                    value={value}
                    onChange={(newValue) => {
                      if (newValue) {
                        onChange(newValue);
                      }
                    }}
                    label={t("common:date_to")}
                    className={"col-span-2"}
                  />
                )}
              />
            )}

            {!getValues("is_all_day") && (
              <>
                <Box className={"grid grid-cols-4 gap-x-xs gap-y-sm py-2"}>
                  {startTimes.map((time) => (
                    <Button
                      variant={
                        !isCustomStartTime &&
                        getTimeId(time.date) == getTimeId(getValues("datetime_start"))
                          ? "primary"
                          : "secondary"
                      }
                      size={"sm"}
                      key={time.id}
                      leftIcon={
                        !isCustomStartTime &&
                        getTimeId(time.date) == getTimeId(getValues("datetime_start")) ? (
                          <FontAwesomeIcon icon={"fa-light fa-check"} />
                        ) : undefined
                      }
                      onClick={() => {
                        const durationCache = durationInMinutes;
                        let dateStart = getValues("datetime_start");
                        dateStart = set(dateStart, {
                          hours: time.date.getHours(),
                          minutes: time.date.getMinutes(),
                        });
                        setValue("datetime_start", dateStart);
                        setValue("datetime_end", add(dateStart, { minutes: durationCache }));
                        setIsCustomStartTime(false);
                      }}
                    >
                      {time.title}
                    </Button>
                  ))}
                  <Button
                    variant={isCustomStartTime ? "primary" : "secondary"}
                    size={"sm"}
                    className={"col-span-2"}
                    onClick={() => {
                      setIsCustomStartTime(true);
                    }}
                  >
                    {t("common:custom")}
                  </Button>
                </Box>
                {!isCustomStartTime && (
                  <Select
                    value={durationInMinutes}
                    emptyTitle={t("common:duration")}
                    label={t("common:duration")}
                    options={durationOptions}
                    showCaption
                    captionPerItem
                    renderSelectedTitle={(items) => {
                      const selected = items[0];
                      if (!selected) return null;
                      const selectedDuration = Number(selected.id);
                      const calculatedEnd = add(dateStartWatch, { minutes: selectedDuration });

                      return (
                        <span className={"font-normal"}>
                          <strong className={"font-semibold"}>{selected.title}</strong> (
                          {renderTime(dateStartWatch)} - {renderTime(calculatedEnd)})
                        </span>
                      );
                    }}
                    onChange={(duration) => {
                      if (duration === "custom") {
                        setIsCustomStartTime(true);
                        return;
                      }

                      if (duration) {
                        setValue(
                          "datetime_end",
                          add(getValues("datetime_start"), {
                            minutes: parseInt(duration as string, 10),
                          })
                        );
                      }
                    }}
                  />
                )}
                {isCustomStartTime && (
                  <Stack direction={"horizontal"} className={"grid grid-cols-2 "} gap={"lg"}>
                    <TimePicker
                      mode={TimePickerMode.single}
                      label={t("calendar:event_start_time")}
                      value={dateStartWatch}
                      showPresets={false}
                      showMask
                      withValueBackground
                      selectTimePartOnFocus
                      onChange={(date) => {
                        const durationCache = durationInMinutes;
                        setValue("datetime_start", date);
                        setValue("datetime_end", add(date, { minutes: durationCache }));
                      }}
                    />

                    <TimePicker
                      mode={TimePickerMode.single}
                      label={t("calendar:event_end_time")}
                      value={dateEndWatch}
                      presetItems={customEndTimePresets}
                      showMask
                      withValueBackground
                      selectTimePartOnFocus
                      navigatePresetsWithArrows
                      onChange={(date) => {
                        setValue("datetime_end", date, { shouldValidate: true });
                      }}
                    />
                  </Stack>
                )}
                {errors && errors.datetime_end?.message && (
                  <Box className={"pt-2 pb-4"}>
                    <Text className={"text-danger"}>{errors.datetime_end.message}</Text>
                  </Box>
                )}
              </>
            )}

            <Controller
              control={control}
              name="timezone"
              render={({ field: { value, onChange } }) => (
                <Select
                  label={t("common:timezone")}
                  options={getTimezones()}
                  emptyTitle={t("common:select")}
                  searchable
                  value={value}
                  onChange={onChange}
                  error={errors.timezone?.message}
                />
              )}
            />
            <Title size={"xs"} header={t("calendar:guests")} paddingTop paddingBottom />
            <Select
              label={t("calendar:add_guests")}
              emptyTitle={t("common:select")}
              value={null}
              options={[]}
              searchable
              showAvatar={true}
              showCaption={true}
              onSearchOptions={(text) => employeesSearch.search(text)}
              searchOptions={employeesSearch.options}
              loadOptionsOnOpen
              onChange={(e) => {
                const addEmployeeAsGuest = employeesSearch.data?.items.find((x) => x.id == e);

                if (addEmployeeAsGuest) {
                  if (!addEmployeeAsGuest.email) {
                    showError({
                      title: t("calendar:employee_no_email", {
                        name: renderEmployeeName(addEmployeeAsGuest),
                      }),
                      button: t("calendar:employee_no_email_open_profile"),
                      onClick: () => window.open(`/employees/${addEmployeeAsGuest.id}`, "_blank"),
                    });
                    return;
                  }
                  setAttendees([
                    ...attendees,
                    {
                      employee: addEmployeeAsGuest,
                      runtime_only_employee: addEmployeeAsGuest,
                      response_status: ApiEventAttendeeResponseStatuses.needs_action,
                      is_organizer: false,
                    },
                  ]);
                }
              }}
              onAddNew={(text) => {
                setAttendees([
                  ...attendees,
                  {
                    email: text,
                    response_status: ApiEventAttendeeResponseStatuses.needs_action,
                    is_organizer: false,
                  },
                ]);
              }}
              addNewPrefix={t("calendar:invite")}
            />
            {!eventId &&
              (attendeeGroups.length > 0 ? (
                <RuleGroupsEditor
                  control={control}
                  setValue={setValue}
                  fieldName="attendee_rule_groups"
                  place={RuleRegistryPlace.eventAttendees}
                  conditionTypes={attendeeConditionTypes.conditionTypes}
                  variant="list"
                  error={errors.attendee_rule_groups?.message}
                />
              ) : (
                <Stack direction={"horizontal"} gap={"xs"} items={"center"} className={"pt-2"}>
                  <Text className={"text-secondary"} size="xs">
                    {t("calendar:guest_group_invite")}
                  </Text>
                  <LinkButtonTo
                    variant={"secondary"}
                    size={"sm"}
                    className={"text-secondary text-xs font-normal underline"}
                    onClick={addFirstGroup}
                  >
                    {t("calendar:guest_group_invite_link")}
                  </LinkButtonTo>
                </Stack>
              ))}
            <Stack className={"py-4"}>
              {attendees.map((attendee, index) => (
                <ListItem
                  key={`attendee-${index}`}
                  avatarUrl={attendee.employee && getEmployeeAvatarUrl(attendee.employee, "sm")}
                  forceShowAvatar={true}
                  title={
                    attendee.employee ? renderEmployeeName(attendee.employee) : attendee.email || ""
                  }
                  captionSlot={
                    <DotSeparator className={"text-secondary"}>
                      {getDescriptionForAttendee(attendee)}
                    </DotSeparator>
                  }
                  valueSlot={
                    <Stack direction="horizontal" gap="sm" items="center">
                      {attendee.employee && conflictingEmployeeIds.has(attendee.employee.id) && (
                        <Tooltip>
                          <TooltipTrigger>
                            <button
                              type="button"
                              aria-label={t("calendar:availability_guest_conflict")}
                              className="text-warning"
                            >
                              <FontAwesomeIcon icon="fa-light fa-triangle-exclamation" />
                            </button>
                          </TooltipTrigger>
                          <TooltipContent className="max-w-80">
                            {t("calendar:availability_guest_conflict")}
                          </TooltipContent>
                        </Tooltip>
                      )}
                      {getValueSlotForAttendee(attendee)}
                    </Stack>
                  }
                />
              ))}
              {suggestedAttendees.map((attendee, index) => (
                <ListItem
                  key={`suggested-attendee-${index}`}
                  titleSlot={
                    <Text
                      className={
                        "text-title overflow-hidden text-ellipsis whitespace-nowrap opacity-50"
                      }
                    >
                      {attendee.email || ""}
                    </Text>
                  }
                  captionSlot={
                    <Text
                      className={
                        "text-title overflow-hidden text-ellipsis whitespace-nowrap opacity-50"
                      }
                    >
                      {t("calendar:invite")}
                    </Text>
                  }
                  avatarUrl={attendee.employee && getEmployeeAvatarUrl(attendee.employee, "sm")}
                  forceShowAvatar={true}
                  valueSlot={
                    <Button
                      variant="secondary"
                      onClick={() => {
                        setAttendees([...attendees, attendee]);
                        setSuggestedAttendees(
                          suggestedAttendees.filter((x) => x.email != attendee.email)
                        );
                      }}
                    >
                      {t("common:add")}
                    </Button>
                  }
                />
              ))}
            </Stack>
            {hasEmployeeGuest && !isExpanded && (
              <Button
                variant="secondary"
                className="mb-4"
                onClick={() => setIsAvailabilityOpen(true)}
              >
                {t("calendar:availability_open")}
              </Button>
            )}
            {conferenceProviders.length > 0 && (
              <Stack className={"pb-4"} gap={"xs"}>
                <Controller
                  control={control}
                  render={({ field: { value, onChange } }) => (
                    <Select
                      label={t("calendar:conference")}
                      emptyTitle={t("calendar:conference")}
                      value={value && conferenceProviders.includes(value) ? value : "none"}
                      options={conferenceOptions}
                      onChange={(newValue) =>
                        onChange(conferenceProviders.find((x) => x === newValue) ?? null)
                      }
                      error={errors.conference_provider?.message}
                    />
                  )}
                  name={"conference_provider"}
                />
                <Text className={"text-secondary"} size={"xs"}>
                  {t("calendar:link_will_be_created_after_event_creation")}
                </Text>
              </Stack>
            )}
            <Title size={"xs"} header={t("common:settings")} paddingTop paddingBottom />
            <Controller
              control={control}
              render={({ field: { value, onChange } }) => (
                <Select
                  disabled={!!eventId}
                  value={value}
                  emptyTitle={t("common:calendar")}
                  onChange={(newValue) => {
                    onChange(newValue);
                    if (calendars) {
                      const calendarFromCalendars = calendars.find((x) => x.id == newValue);
                      if (calendarFromCalendars) {
                        if (!dirtyFields.timezone) {
                          setValue("timezone", calendarFromCalendars.timezone);
                        }
                        setCalendar(calendarFromCalendars);
                      }
                    }
                  }}
                  options={calendars}
                  error={errors.calendar_id?.message}
                  searchable
                />
              )}
              name={"calendar_id"}
            />
            <Controller
              control={control}
              render={({ field: { value, onChange } }) => (
                <Select
                  value={value == ApiEventVisibility.private ? "private" : "public"}
                  emptyTitle={t("calendar:event_visibility")}
                  onChange={(newValue) => {
                    onChange(newValue as ApiEventVisibility);
                  }}
                  options={privacyOptions}
                  error={errors.visibility?.message}
                  searchable
                />
              )}
              name={"visibility"}
            />
            <Textarea
              label={t("common:description")}
              maxHeight={150}
              placeholder={t("common:description")}
              {...register("description")}
            />
          </div>
          {isExpanded && (
            <div
              className="event-editor-save sticky bottom-0 z-20 mt-auto shrink-0 border-t border-dark/10 bg-light p-4"
              data-state={availabilityStatus}
              inert={availabilityStatus === "close"}
            >
              {saveActions}
            </div>
          )}
        </form>
        {isExpanded && (
          <div
            ref={availabilityRefs.setFloating}
            className="event-editor-availability min-h-0 min-w-0 flex-1"
            data-state={availabilityStatus}
            inert={availabilityStatus === "close"}
          >
            <EventAvailabilityPanel
              attendees={attendees}
              start={dateStartWatch}
              end={dateEndWatch}
              timezone={timezoneWatch}
              isAllDay={isAllDayWatch}
              onClose={() => setIsAvailabilityOpen(false)}
              isEditing={!!eventId}
              hasGroups={attendeeGroups.some((group) => group.rules.length > 0)}
              onSelect={(start, end) => {
                setValue("is_all_day", false, { shouldDirty: true });
                setValue("datetime_start", start, { shouldDirty: true, shouldValidate: true });
                setValue("datetime_end", end, { shouldDirty: true, shouldValidate: true });
                setIsCustomStartTime(true);
              }}
            />
          </div>
        )}
      </div>
    </Modal>
  );
}
